import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyEmployees,
  CompanyEmployeesDraftPage,
  CompanyEmployeesInstantPage,
  CompanyEmployeesActivePage,
  CompanyEmployeesAllPage,
  CompanyEmployeesOffboardedPage,
  CompanyUpsertEmployees,
  CompanyCreateEmployee,
  CompanyViewEmployee,
  CompanyInstantOnboarding,
} from "@/utils/lazyLoadComponents";
import { store } from "@/store";
import { companyUserApi } from "@/services/(company)/userApi";
import { USER_ACCOUNT_STATUS_ENUM } from "@/utils/enums";
import { COMPANY_ONBOARDING_PATHS } from "./routes.onboarding";

// ─── Path Segments ────────────────────────────────────────────────────────────
export enum COMPANY_EMPLOYEES_PATH_SEGMENTS {
  ROOT = "employees",
  DRAFT = "draft",
  INSTANT = "instant",
  ACTIVE = "active",
  ALL = "all-employees",
  OFFBOARDED = "offboarded",
  ADD = "add",
  ONBOARDING = "onboarding",
  INSTANT_ONBOARDING = "instant-onboarding",
  VIEW = "view",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyEmployeesRoutes: RouteObject = {
  path: COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Employees" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyEmployees,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_EMPLOYEES_PATHS.ACTIVE),
        },
        {
          path: COMPANY_EMPLOYEES_PATH_SEGMENTS.DRAFT,
          handle: { breadcrumb: "Draft" },
          Component: CompanyEmployeesDraftPage,
        },
        {
          path: COMPANY_EMPLOYEES_PATH_SEGMENTS.INSTANT,
          handle: { breadcrumb: "Instant Onboarded" },
          Component: CompanyEmployeesInstantPage,
        },
        {
          path: COMPANY_EMPLOYEES_PATH_SEGMENTS.ACTIVE,
          handle: { breadcrumb: "Active Employees" },
          Component: CompanyEmployeesActivePage,
        },
        {
          // Legacy slug (HM-1012): redirect old bookmarked/shared links.
          path: "list-of-employees",
          loader: () => redirect(COMPANY_EMPLOYEES_PATHS.ACTIVE),
        },
        {
          path: COMPANY_EMPLOYEES_PATH_SEGMENTS.ALL,
          handle: { breadcrumb: "All Employees" },
          Component: CompanyEmployeesAllPage,
        },
        {
          path: COMPANY_EMPLOYEES_PATH_SEGMENTS.OFFBOARDED,
          handle: { breadcrumb: "Offboarded" },
          Component: CompanyEmployeesOffboardedPage,
        },
      ],
    },
    {
      path: COMPANY_EMPLOYEES_PATH_SEGMENTS.ADD,
      handle: { breadcrumb: "Add Employee" },
      Component: CompanyCreateEmployee,
    },
    {
      path: COMPANY_EMPLOYEES_PATH_SEGMENTS.ONBOARDING,
      handle: { breadcrumb: "Employee Details" },
      Component: CompanyUpsertEmployees,
    },
    {
      path: COMPANY_EMPLOYEES_PATH_SEGMENTS.INSTANT_ONBOARDING,
      handle: { breadcrumb: "Instant Onboarding" },
      Component: CompanyInstantOnboarding,
    },
    {
      path: COMPANY_EMPLOYEES_PATH_SEGMENTS.VIEW,
      loader: async ({ request }: { request: Request }) => {
        const id = new URL(request.url).searchParams.get("id");
        if (!id) return { breadcrumbLabel: "Employee Profile" };
        try {
          const result = await store.dispatch(
            companyUserApi.endpoints.getUsersById.initiate({ id }),
          );
          const emp = result.data?.data;
          const name = emp
            ? `${emp.firstName ?? ""} ${emp.lastName ?? ""}`.trim()
            : null;
          return { breadcrumbLabel: name || "Employee Profile" };
        } catch {
          return { breadcrumbLabel: "Employee Profile" };
        }
      },
      shouldRevalidate: ({ currentUrl, nextUrl }) =>
        currentUrl.searchParams.get("id") !== nextUrl.searchParams.get("id"),
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) =>
          data?.breadcrumbLabel ?? "Employee Profile",
      },
      Component: CompanyViewEmployee,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_EMPLOYEES_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT)),
  DRAFT: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.DRAFT,
    ),
  ),
  INSTANT: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.INSTANT,
    ),
  ),
  ACTIVE: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ACTIVE,
    ),
  ),
  ALL: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ALL,
    ),
  ),
  OFFBOARDED: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.OFFBOARDED,
    ),
  ),
  ADD: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ADD,
    ),
  ),
  ONBOARDING: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ONBOARDING,
    ),
  ),
  INSTANT_ONBOARDING: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.INSTANT_ONBOARDING,
    ),
  ),
  VIEW: withCompanyPrefix(
    addSlashes(
      COMPANY_EMPLOYEES_PATH_SEGMENTS.ROOT,
      COMPANY_EMPLOYEES_PATH_SEGMENTS.VIEW,
    ),
  ),
});

/**
 * Resolves the right employee form route for a given status:
 * - draft / instant → Add Employee (2-tab Personal + Employment)
 * - onboarding / pending → Onboarding (5-tab Compensation → Digital Access)
 * - everything else (active, etc.) → full edit (legacy upsert route)
 */
export const employeeFormPath = (
  id: number | string,
  status?: string,
): string => {
  if (
    status === USER_ACCOUNT_STATUS_ENUM.DRAFT ||
    status === USER_ACCOUNT_STATUS_ENUM.INSTANT
  ) {
    return `${COMPANY_EMPLOYEES_PATHS.ADD}?id=${id}`;
  }
  if (
    status === USER_ACCOUNT_STATUS_ENUM.ONBOARDING ||
    status === USER_ACCOUNT_STATUS_ENUM.PENDING
  ) {
    return COMPANY_ONBOARDING_PATHS.EMPLOYEE(id);
  }
  return `${COMPANY_EMPLOYEES_PATHS.ONBOARDING}?id=${id}`;
};
